import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { Page } from "@/components/page";
import { Button } from "@/components/ui/button";
import {
  type RoadmapOverview,
  type RoadmapTriageEntry,
  useMe,
  useRefetchRoadmap,
  useRoadmap,
} from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { formatMonth } from "@/lib/format";
import { byEmail } from "@/lib/people";
import { isDeveloper, seesRoadmapHours } from "@/lib/roles";
import { toast } from "@/lib/toast";

import { CloseTriageDialog } from "./close-triage-dialog";
import { DeleteTriageDialog } from "./delete-triage-dialog";
import { focusFell } from "./details-parts";
import { ItemDetailsDialog } from "./item-details";
import { MergeTriageDialog } from "./merge-triage-dialog";
import { MonthBlock } from "./month-block";
import { NewIdeaDialog } from "./new-idea-dialog";
import { RoadmapFiltersBar } from "./roadmap-filters";
import { RoadmapTable, type RowFailure } from "./roadmap-table";
import {
  focusTriageToggle,
  shownTriage,
  type TriageAction,
  triageEntryGone,
  triagePeople,
} from "./triage";
import {
  budgetEdit,
  currentMonth,
  foldsWhile,
  followFailureMessage,
  hasRoadmapFilters,
  INITIAL_MONTH_FOLDS,
  inMonthView,
  matchesRoadmapFilters,
  type MonthFolds,
  monthKind,
  monthStatusGroups,
  movedFocusSelectors,
  type MovedItem,
  nextSort,
  NO_ROADMAP_FILTERS,
  parseRoadmapView,
  planMonthFor,
  type RoadmapView as RoadmapUrlView,
  roadmapViewParams,
  shownFolds,
  shownSort,
  sortItems,
  sortSummary,
  toggleCounts,
  toggleGroupFold,
} from "./utils";

/**
 * Roadmap: every item De Ceuster wants from DC Data, by status, with the
 * month's work, against its budget for an owner and a developer. For admins,
 * owners and developers only (its route sits behind `<AdminRoute>`).
 *
 * The month on show, the filters and the column sort live in the URL
 * (`?month=2026-11&q=export&priority=high&blocked=1&sort=effort&dir=desc`),
 * so a reload or a shared link keeps them. A developer's "Nieuw idee" adds an
 * idea, and from a past month, which shows no ideas, it steps to the current
 * month to show it; anyone else's "Idee voorstellen" passes one on to the
 * triage, and the page thanks them. An item's title, or its segment of the
 * month's bar, opens its details, where the viewer does what the server
 * allows them. A developer also moves an item's status from the symbol in
 * front of its title. Naar backlog on a triage entry opens the developer's
 * new idea dialog, starting from the entry; its other actions ask in a dialog
 * of the roadmap view's own.
 */
export function RoadmapPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseRoadmapView(searchParams);
  const query = useRoadmap(view.month);
  const overview = query.data;
  const me = useMe().data;
  // The roadmap's own word on the viewer's role, else the session's while it loads.
  const role = overview?.viewer.role ?? me?.role;
  const developer = isDeveloper(role);
  // The new idea's dialog, and the triage entry it starts from; the entry stays while it closes.
  const [creating, setCreating] = useState<{ open: boolean; entry: RoadmapTriageEntry | null }>({
    open: false,
    entry: null,
  });
  // Naar backlog names a submitter as the entry's row does: by their account's name when the entry lacks one.
  const people = overview
    ? triagePeople(overview.triage ?? [], byEmail(overview.people))
    : undefined;
  const setView = (next: RoadmapUrlView) =>
    setSearchParams(roadmapViewParams(next), { replace: true });
  const onCreated = () => {
    if (overview && monthKind(overview.month.month, currentMonth()) === "past") {
      setView({ ...view, month: null });
    }
  };

  return (
    <Page
      title={t("nav.roadmap")}
      actions={
        <Button size="sm" onClick={() => setCreating({ open: true, entry: null })}>
          <Plus aria-hidden="true" className="size-4" />
          {developer ? t("roadmap.newIdea") : t("roadmap.proposeIdea")}
        </Button>
      }
    >
      {query.isLoading ? (
        <LoadingCard />
      ) : !overview ? (
        <LoadErrorCard message={t("roadmap.loadError")} onRetry={() => void query.refetch()} />
      ) : (
        <RoadmapView
          overview={overview}
          view={view}
          onViewChange={setView}
          monthLoading={query.isPlaceholderData}
          onAcceptEntry={(entry) => setCreating({ open: true, entry })}
        />
      )}
      <NewIdeaDialog
        open={creating.open}
        developer={developer}
        entry={creating.entry}
        entryGone={triageEntryGone(creating.entry, overview?.triage)}
        people={people}
        onOpenChange={(open) => setCreating((current) => ({ ...current, open }))}
        onCreated={onCreated}
        onProposed={() =>
          toast.success({ key: "roadmap.form.proposed" }, { durationMs: PROPOSED_NOTICE_MS })
        }
      />
    </Page>
  );
}

/** How long the thanks for a proposed idea stays: it is a sentence or two. */
const PROPOSED_NOTICE_MS = 8_000;

interface RoadmapViewProps {
  overview: RoadmapOverview;
  /** The month, the filters and the sort, as the URL holds them. */
  view: RoadmapUrlView;
  onViewChange: (view: RoadmapUrlView) => void;
  /** While another month's overview loads and this one stays on screen. */
  monthLoading?: boolean;
  /** Naar backlog on a triage entry: the page opens the new idea's dialog for it. */
  onAcceptEntry: (entry: RoadmapTriageEntry) => void;
}

/** The actions on a triage entry that ask in a dialog of the roadmap view's own. */
type EntryDialogAction = Exclude<TriageAction, "accept">;

/** How long the search waits after the last key before it writes the URL. */
const SEARCH_URL_DELAY_MS = 300;

/**
 * The page once the roadmap is in: the month block, the filter row and the
 * table, and an item's details over them. The table shows what belongs to
 * the month on show (`monthStatusGroups`): a past month what was done in it
 * and nothing else, the current month what was done in it, what is in
 * progress, what is planned in it and the ideas, a later month what is
 * planned in it and the ideas. The filters and their counts work on those
 * items. Nothing changes in a past month: an item opened from its bar shows
 * no follow button and no actions, but for a developer's own part. While a
 * column sort is active, a line above the table says what the list is sorted
 * on, with a button back to the default order. The search box keeps its own
 * text: the table follows it at once and the URL after a pause, as on the
 * Tools page, so the box never jumps its cursor.
 *
 * In the current month Klaar starts folded, but a search or filter opens
 * every group, so a hit there shows as a row; its folds last until it
 * clears, and the ones from before come back (`foldsWhile`). Each kind of
 * month keeps its own folds. After a move from a row's status menu, the
 * focus follows the item to its new group.
 *
 * A developer's triage, the ideas others proposed and the questions DC Data
 * could not answer, shows as a group above the ideas in the current month and
 * later ones (`shownTriage`); nobody else gets it from the server. When an
 * entry leaves (put on the roadmap, merged, closed or deleted) and the focus
 * fell with its row, it goes to the group's toggle, or to the ideas' when it
 * was the last.
 * Every action on an entry (Naar backlog, Samenvoegen met…, Sluiten and
 * Verwijderen) refetches the roadmap as its dialog opens: the page never
 * refetches on focus, and the entry may have been handled from Claude
 * meanwhile, which the dialog then says before anything is typed.
 *
 * Only an owner and a developer see hours and days (`seesRoadmapHours`). An
 * admin's roadmap carries none: the month block shows the bar alone, the
 * table has no effort column, and a link sorted on the effort keeps the
 * default order for them.
 */
export function RoadmapView({
  overview,
  view,
  onViewChange,
  monthLoading = false,
  onAcceptEntry,
}: RoadmapViewProps) {
  const { t } = useTranslation();
  const { filters } = view;
  const viewer = overview.viewer.email;
  const showHours = seesRoadmapHours(overview.viewer.role);
  const sort = shownSort(view.sort, showHours);
  // The month whose items are on screen, and where it sits against the current month in Belgium.
  const month = overview.month.month;
  const current = currentMonth();
  const kind = monthKind(month, current);
  const [search, setSearch] = useState(filters.search);
  const shownFilters = { ...filters, search };
  const filtering = hasRoadmapFilters(shownFilters);
  const [storedFolds, setFolds] = useState<MonthFolds>(() => ({
    ...INITIAL_MONTH_FOLDS,
    [kind]: foldsWhile(INITIAL_MONTH_FOLDS[kind], filtering),
  }));
  // A search or filter that starts or ends switches the folds in this render, before anything shows.
  const folds = foldsWhile(storedFolds[kind], filtering);
  if (folds !== storedFolds[kind]) {
    setFolds({ ...storedFolds, [kind]: folds });
  }
  // The item whose details are open; its id stays while the dialog closes.
  const [selected, setSelected] = useState<{ id: string; open: boolean } | null>(null);
  // A follow or a status move in the table that failed, worded at render so a language switch re-words it.
  const [rowFailure, setRowFailure] = useState<RowFailure | null>(null);
  // The item last moved from a row's status menu, and the move the focus already followed.
  const [moved, setMoved] = useState<MovedItem | null>(null);
  const followedMove = useRef<MovedItem | null>(null);
  const tableArea = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (search === filters.search) {
      return;
    }
    const timer = setTimeout(
      () => onViewChange({ ...view, filters: { ...filters, search } }),
      SEARCH_URL_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [search, filters, view, onViewChange]);

  // The entry a dialog from its row asks about, and which one; both stay while the dialog closes.
  const [entryDialog, setEntryDialog] = useState<{
    action: EntryDialogAction;
    entry: RoadmapTriageEntry | null;
    open: boolean;
  }>({ action: "close", entry: null, open: false });
  const setEntryDialogOpen = (open: boolean) => setEntryDialog((current) => ({ ...current, open }));
  const entryGone = triageEntryGone(entryDialog.entry, overview.triage);
  const refetchRoadmap = useRefetchRoadmap();
  const triage = shownTriage(overview.triage ?? [], shownFilters, kind);
  const triageCount = overview.triage?.length ?? 0;
  const hadTriage = useRef(triageCount);

  useEffect(() => {
    const fewer = triageCount < hadTriage.current;
    hadTriage.current = triageCount;
    if (fewer && focusFell()) {
      focusTriageToggle(tableArea.current);
    }
  }, [triageCount]);

  const people = byEmail(overview.people);
  // The triage also counts its submitters without an account when two share a first name.
  const submitters = triagePeople(overview.triage ?? [], people);
  const itemsById = new Map(overview.items.map((item) => [item.id, item]));
  const movedStatus = moved ? itemsById.get(moved.id)?.status : undefined;

  // The moved item's row left the table with the move: once the roadmap shows it in its new
  // group, the focus goes to its status symbol there, or to that group's toggle when it is folded.
  useEffect(() => {
    if (!moved || movedStatus !== moved.status || followedMove.current === moved) {
      return;
    }
    followedMove.current = moved;
    for (const selector of movedFocusSelectors(moved)) {
      const target = tableArea.current?.querySelector<HTMLElement>(selector);
      if (target) {
        target.focus();
        return;
      }
    }
  }, [moved, movedStatus]);

  const onShow = overview.items.filter((item) => inMonthView(item, month, current));
  const matching = onShow.filter((item) => matchesRoadmapFilters(item, shownFilters, viewer));
  const groups = monthStatusGroups(matching, month, current).map((group) => ({
    ...group,
    items: sortItems(group.items, sort),
  }));
  const selectedItem = selected ? (itemsById.get(selected.id) ?? null) : null;
  const planMonth = planMonthFor(month, current);
  const emptyText = filtering
    ? t("roadmap.filter.empty")
    : overview.items.length === 0
      ? isDeveloper(overview.viewer.role)
        ? t("roadmap.empty")
        : t("roadmap.emptyPropose")
      : t(kind === "past" ? "roadmap.month.emptyPast" : "roadmap.month.empty", {
          month: formatMonth(month),
        });

  const changeFilters = (next: typeof filters) => {
    setSearch(next.search);
    // Another filter changed: write it at once, with the search as typed.
    if (next.search === search) {
      onViewChange({ ...view, filters: next });
    }
  };
  const reset = () => {
    setSearch("");
    onViewChange({ ...view, filters: NO_ROADMAP_FILTERS });
  };
  const openItem = (id: string) => {
    setRowFailure(null);
    setSelected({ id, open: true });
  };
  const changeMonth = (next: string) =>
    onViewChange({
      ...view,
      filters: shownFilters,
      // The current month is the page's default, so it leaves the URL.
      month: next === current ? null : next,
    });

  return (
    <div className="space-y-6">
      <MonthBlock
        month={overview.month}
        // While another month loads, the month asked for, so ‹ › step on from it.
        shownMonth={monthLoading ? (view.month ?? current) : month}
        kind={kind}
        budgetEdit={budgetEdit(overview.viewer, month, current)}
        onMonthChange={changeMonth}
        loading={monthLoading}
        onOpenItem={openItem}
      />
      <div className="space-y-4">
        <RoadmapFiltersBar
          filters={shownFilters}
          onChange={changeFilters}
          onReset={reset}
          counts={toggleCounts(onShow, shownFilters, viewer)}
          isDeveloper={overview.viewer.is_developer}
        />
        {rowFailure && (
          <p role="alert" className="text-destructive text-sm">
            {rowFailure.action === "followRoadmapItem"
              ? followFailureMessage(rowFailure.error)
              : apiErrorMessage(rowFailure.error, rowFailure.action)}
          </p>
        )}
        <div ref={tableArea} className="space-y-2">
          {sort && (
            <p className="text-muted flex flex-wrap items-baseline gap-x-2 text-xs">
              <span>{sortSummary(sort)}</span>
              <button
                type="button"
                onClick={() => onViewChange({ ...view, filters: shownFilters, sort: null })}
                className="text-accent focus-visible:ring-ring tap-target rounded-sm font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
              >
                {t("roadmap.sort.reset")}
              </button>
            </p>
          )}
          <RoadmapTable
            groups={groups}
            triage={triage}
            onEntryAction={(action, entry) => {
              refetchRoadmap();
              if (action === "accept") {
                onAcceptEntry(entry);
              } else {
                setEntryDialog({ action, entry, open: true });
              }
            }}
            sort={sort}
            onSort={(column) =>
              onViewChange({ ...view, filters: shownFilters, sort: nextSort(sort, column) })
            }
            folded={shownFolds(folds)}
            onToggleGroup={(status) =>
              setFolds({ ...storedFolds, [kind]: toggleGroupFold(folds, status) })
            }
            emptyText={emptyText}
            onReset={filtering ? reset : undefined}
            viewer={viewer}
            isDeveloper={overview.viewer.is_developer}
            showHours={showHours}
            planMonth={planMonth}
            people={people}
            submitters={submitters}
            items={itemsById}
            search={search}
            onOpen={openItem}
            onError={(failure) => setRowFailure(inlineFailure(failure))}
            onClearError={() => setRowFailure(null)}
            onMoved={setMoved}
          />
        </div>
      </div>
      <ItemDetailsDialog
        item={selectedItem}
        open={selected?.open ?? false}
        onOpenChange={(open) => setSelected((current) => current && { ...current, open })}
        overview={overview}
        planMonth={planMonth}
        pastMonth={kind === "past"}
        onOpenItem={openItem}
      />
      <CloseTriageDialog
        entry={entryDialog.entry}
        entryGone={entryGone}
        open={entryDialog.open && entryDialog.action === "close"}
        onOpenChange={setEntryDialogOpen}
      />
      <MergeTriageDialog
        entry={entryDialog.entry}
        entryGone={entryGone}
        items={overview.items}
        open={entryDialog.open && entryDialog.action === "merge"}
        onOpenChange={setEntryDialogOpen}
      />
      <DeleteTriageDialog
        entry={entryDialog.entry}
        entryGone={entryGone}
        people={submitters}
        open={entryDialog.open && entryDialog.action === "delete"}
        onOpenChange={setEntryDialogOpen}
      />
    </div>
  );
}
